import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import {
    LiveKitRoom,
    RoomAudioRenderer,
    useConnectionState,
    useLocalParticipant,
    useMultibandTrackVolume,
    useRemoteParticipants,
    useRoomContext,
    useTracks,
} from '@livekit/components-react';
import { ConnectionState, type DisconnectReason, Track } from 'livekit-client';
import FontAwesome from '@expo/vector-icons/FontAwesome';

import { ENV } from '@/config/env';
import { Colors } from '@/constants/theme';
import { useCallToken } from '@/hooks/useCallToken';
import { useCallAnswered } from '@/hooks/useCallAnswered';
import { useRingback } from '@/hooks/useRingback';
import { PREVIOUS_CALL_CLOSING_MESSAGE, submitCallFeedback, terminateCallWithRetry } from '@/services/calls/client';
import { parsePhoneIdParam } from '@/services/calls/callParams';
import type { CallChannel, CallFeedback } from '@/services/calls/types';
import {
    CALL_FAILED_MESSAGE,
    classifyDisconnect,
    describeDisconnect,
    disconnectReasonName,
    reportCallFailure,
} from '@/services/calls/callFailure';
import { CallFeedbackForm } from '@/components/calls/CallFeedbackForm';

/**
 * In-call screen (web / Electron desktop). Designed to run in its own desktop
 * window (opened via `window.desktop.openCall`), styled as a compact call card:
 * avatar, name, the number being dialled, Ringing→timer, an animated waveform
 * when connected, and pill controls (device picker / mute / hang up). The
 * device picker works from the moment the room is joined, so mic / speaker
 * can be set while it still rings. Uses LiveKit's web stack
 * (@livekit/components-react). Voice only.
 *
 * Ending the call: the backend keeps the client's leg alive until it is told
 * otherwise, so every guide-side end must reach `POST /call/terminate` — the
 * hang-up / cancel buttons, a failed join or dropped connection (see
 * [services/calls/callFailure.ts]) and the window or tab going away
 * (`pagehide`, sent with `keepalive` so it outlives the page). Only
 * remote-ended calls skip it.
 *
 * After the call: once a call that was actually answered ends (or fails and
 * the guide closes the error), the window stays open on the quality survey
 * ([CallFeedbackForm] → `POST /call/feedback`) instead of closing itself;
 * calls nobody picked up have nothing to rate and close as before.
 */
export default function CallScreenWeb() {
    const router = useRouter();
    const params = useLocalSearchParams<{
        channel?: string;
        channel_id?: string;
        name?: string;
        phone?: string;
        image?: string;
        phone_id?: string;
    }>();

    const channel = params.channel as CallChannel;
    const channelId = params.channel_id ?? '';
    const calleeName = params.name || 'Contact';
    // The number actually being dialled: the picked PSTN number when the guide
    // chose one, otherwise the client's WhatsApp number. Shown under the name
    // so the guide can tell which of a client's numbers this call is going to.
    const phone = params.phone ?? '';
    const image = params.image || '';
    // Route params arrive as strings; absent/garbage stays `undefined` so the
    // token request omits `phone_id` rather than sending NaN or "undefined".
    const phoneId = parsePhoneIdParam(params.phone_id);

    const [ended, setEnded] = useState(false);
    // Set when the call died on us (failed join, dropped connection): shows an
    // error with Retry instead of the auto-closing "Call ended".
    const [failure, setFailure] = useState<string | null>(null);
    // The room to rate: set instead of the auto-close timer when an answered
    // call ends, and the survey then owns closing the window.
    const [feedbackRoom, setFeedbackRoom] = useState<string | null>(null);
    const endedRef = useRef(false);
    const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Latched by the room content once the other party actually picks up —
    // only those calls get asked how they went.
    const answeredRef = useRef(false);

    const { status, credentials, error, retry } = useCallToken({ channel, channel_id: channelId, phone_id: phoneId });
    const roomName = credentials?.roomName;

    // Hang up: close the desktop call window if we're in the Electron shell,
    // otherwise fall back to in-app navigation (plain browser).
    const leaveScreen = useCallback(() => {
        if (typeof window !== 'undefined' && window.desktop) {
            (window.desktop.closeCall ?? (() => window.close()))();
            return;
        }
        if (router.canGoBack()) router.back();
        else router.replace('/(tabs)');
    }, [router]);

    // Idempotent end-of-call: show "ended", then either ask for feedback
    // (answered call) or close. `roomName` is a safe dep: it only changes while
    // no room is mounted (token fetch / retry).
    const endCall = useCallback(() => {
        if (endedRef.current) return;
        endedRef.current = true;
        setEnded(true);
        if (answeredRef.current && roomName) {
            setFeedbackRoom(roomName);
            return;
        }
        endTimerRef.current = setTimeout(leaveScreen, 1000);
    }, [leaveScreen, roomName]);

    const markAnswered = useCallback(() => {
        answeredRef.current = true;
    }, []);

    // Close from the failure view: a call that had connected before dropping is
    // exactly the kind worth rating ("Call connected but dropped").
    const closeAfterFailure = useCallback(() => {
        if (answeredRef.current && roomName) {
            setFailure(null);
            setFeedbackRoom(roomName);
            return;
        }
        leaveScreen();
    }, [leaveScreen, roomName]);

    const submitFeedback = useCallback(async (feedback: CallFeedback) => {
        if (!feedbackRoom) return;
        // Throws on failure — the form shows the message and offers Skip.
        await submitCallFeedback(feedbackRoom, feedback);
        leaveScreen();
    }, [feedbackRoom, leaveScreen]);

    // Tear down the bridged WhatsApp/PSTN leg on the backend. Guarded so the
    // various guide-side end paths (in-call hang up, pre-connect cancel, failed
    // join, dropped connection, page hide) only fire it once. If the other side
    // hangs up first the backend already tears its own leg down, so those paths
    // don't call this. `keepalive` lets it finish even when the call window is
    // closing underneath it; retries on network/5xx failures cover a short
    // outage while the page is still alive.
    const terminatedRef = useRef(false);
    const terminateBackendCall = useCallback(() => {
        if (terminatedRef.current) return;
        if (!roomName) return;
        terminatedRef.current = true;
        terminateCallWithRetry(roomName, { keepalive: true }, (e, attempt) =>
            console.log(`Terminate call failed (attempt ${attempt}), retrying:`, (e as Error)?.message),
        ).catch((e) => {
            console.log('Terminate call error:', e);
            reportCallFailure(e, { stage: 'terminate', roomName });
        });
    }, [roomName]);

    // Hang up before the room is even connected (still fetching token / ringing):
    // the WhatsApp leg was already dialed server-side, so terminate it too.
    const cancelCall = useCallback(() => {
        terminateBackendCall();
        endCall();
    }, [terminateBackendCall, endCall]);

    // The call died on us: hang up the client's leg (nobody else will) and keep
    // the window open on an error the guide can act on. Shares `endedRef` with
    // `endCall` so whichever fires first wins.
    const failCall = useCallback((message: string) => {
        if (endedRef.current) return;
        endedRef.current = true;
        terminateBackendCall();
        setFailure(message);
    }, [terminateBackendCall]);

    // Start over after a failure: fresh guards, new token (a new call).
    const retryCall = useCallback(() => {
        endedRef.current = false;
        terminatedRef.current = false;
        answeredRef.current = false;
        setFailure(null);
        setEnded(false);
        setFeedbackRoom(null);
        retry();
    }, [retry]);

    // Memoised (not inline) on purpose: LiveKitRoom re-runs its connect effect
    // whenever these callbacks change identity.
    const handleDisconnected = useCallback((reason?: DisconnectReason) => {
        const outcome = classifyDisconnect(reason);
        if (outcome === 'failed') {
            const name = disconnectReasonName(reason);
            console.log('LiveKit disconnected unexpectedly:', name);
            reportCallFailure(`LiveKit disconnected: ${name}`, { stage: 'disconnect', reason: name, roomName });
            failCall(describeDisconnect(reason));
            return;
        }
        endCall();
    }, [endCall, failCall, roomName]);

    const handleRoomError = useCallback((e: Error) => {
        console.log('LiveKit room error:', e?.message);
        reportCallFailure(e, { stage: 'room', roomName });
        failCall(CALL_FAILED_MESSAGE);
    }, [failCall, roomName]);

    useEffect(
        () => () => {
            if (endTimerRef.current) clearTimeout(endTimerRef.current);
        },
        [],
    );

    // The call window / tab going away (close button, reload, navigation) must
    // still hang up the client's leg. livekit-client leaves the room on its own
    // here, but the backend is never told. Skipped once the call has already
    // ended by any other path (`endedRef`), so a remote hang-up or a finished
    // call doesn't send a redundant terminate.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onPageHide = () => {
            if (!endedRef.current) terminateBackendCall();
        };
        window.addEventListener('pagehide', onPageHide);
        return () => window.removeEventListener('pagehide', onPageHide);
    }, [terminateBackendCall]);

    const livekitReady = status === 'success' && credentials && !!ENV.LIVEKIT_URL;

    let body: React.ReactNode;
    if (feedbackRoom) {
        body = <FeedbackCard name={calleeName} image={image} onSubmit={submitFeedback} onSkip={leaveScreen} />;
    } else if (failure) {
        body = (
            <CallCard
                name={calleeName}
                phone={phone}
                image={image}
                status={failure}
                controls={
                    <ControlsRow>
                        <PillButton variant="neutral" icon="refresh" onPress={retryCall} />
                        <HangupButton onPress={closeAfterFailure} />
                    </ControlsRow>
                }
            />
        );
    } else if (ended) {
        body = <CallCard name={calleeName} phone={phone} image={image} status="Call ended" controls={null} />;
    } else if (!ENV.LIVEKIT_URL) {
        body = (
            <CallCard
                name={calleeName}
                phone={phone}
                image={image}
                status="Calling isn’t configured"
                controls={<HangupButton onPress={leaveScreen} />}
            />
        );
    } else if (status === 'error') {
        body = (
            <CallCard
                name={calleeName}
                phone={phone}
                image={image}
                status={error ?? 'Could not start the call'}
                controls={
                    <ControlsRow>
                        <PillButton variant="neutral" icon="refresh" onPress={retry} />
                        <HangupButton onPress={leaveScreen} />
                    </ControlsRow>
                }
            />
        );
    } else if (status === 'waiting') {
        // The previous call with this client is still closing on the backend;
        // the hook is retrying the token. No call exists yet, so leaving needs
        // no terminate.
        body = (
            <CallCard
                name={calleeName}
                phone={phone}
                image={image}
                status={PREVIOUS_CALL_CLOSING_MESSAGE}
                controls={
                    <ControlsRow>
                        <PillButton variant="neutral" icon="ellipsis-h" disabled />
                        <PillButton variant="accent" icon="microphone" disabled />
                        <HangupButton onPress={leaveScreen} />
                    </ControlsRow>
                }
            />
        );
    } else if (!livekitReady) {
        body = (
            <CallCard
                name={calleeName}
                phone={phone}
                image={image}
                status="Ringing.."
                controls={
                    <ControlsRow>
                        <PillButton variant="neutral" icon="ellipsis-h" disabled />
                        <PillButton variant="accent" icon="microphone" disabled />
                        <HangupButton onPress={cancelCall} />
                    </ControlsRow>
                }
            />
        );
    } else {
        body = (
            <LiveKitRoom
                serverUrl={ENV.LIVEKIT_URL}
                token={credentials!.token}
                connect
                audio
                video={false}
                onDisconnected={handleDisconnected}
                onError={handleRoomError}
                style={{ display: 'contents' }}
            >
                <RoomAudioRenderer />
                <CallRoomContent
                    name={calleeName}
                    phone={phone}
                    image={image}
                    onEnd={endCall}
                    onTerminate={terminateBackendCall}
                    onAnswered={markAnswered}
                />
            </LiveKitRoom>
        );
    }

    return (
        <View style={styles.window}>
            <Stack.Screen options={{ headerShown: false }} />
            {body}
        </View>
    );
}

interface CallRoomContentProps {
    name: string;
    phone: string;
    image: string;
    onEnd: () => void;
    /** Tear down the bridged WhatsApp leg (guide-initiated hang up). */
    onTerminate: () => void;
    /** Called once the other party has actually picked up. */
    onAnswered: () => void;
}

/** Lives inside <LiveKitRoom>; reads room state via @livekit/components-react. */
function CallRoomContent({ name, phone, image, onEnd, onTerminate, onAnswered }: CallRoomContentProps) {
    const room = useRoomContext();
    const connectionState = useConnectionState();
    const remoteParticipants = useRemoteParticipants();
    const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();

    // A joined participant is still just dialing/ringing; the real "answered"
    // signal is channel-specific (WhatsApp metadata / PSTN sip.callStatus).
    const answered = useCallAnswered(room);

    // Ring while dialing, until the other side answers.
    useRingback(!answered);

    // Let the screen know the call was picked up (it decides on feedback later).
    useEffect(() => {
        if (answered) onAnswered();
    }, [answered, onAnswered]);

    const hadRemoteRef = useRef(false);
    const [seconds, setSeconds] = useState(0);
    const [pickerOpen, setPickerOpen] = useState(false);

    // End once everyone else has left (after someone had joined).
    useEffect(() => {
        if (remoteParticipants.length > 0) hadRemoteRef.current = true;
        else if (hadRemoteRef.current) onEnd();
    }, [remoteParticipants.length, onEnd]);

    // Call timer — starts only once the call is actually answered.
    useEffect(() => {
        if (!answered) return;
        const id = setInterval(() => setSeconds((s) => s + 1), 1000);
        return () => clearInterval(id);
    }, [answered]);

    const toggleMute = useCallback(async () => {
        try {
            await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
        } catch (e) {
            console.log('Mute toggle error:', e);
        }
    }, [localParticipant, isMicrophoneEnabled]);

    const handleHangup = useCallback(async () => {
        // Disconnecting our own LiveKit room does not end the bridged WhatsApp
        // leg — tell the backend to tear it down.
        onTerminate();
        try {
            await room.disconnect();
        } catch (e) {
            console.log('Disconnect error:', e);
        }
        onEnd();
    }, [room, onEnd, onTerminate]);

    // Both reconnect flavours (full reconnect and signal-only resume) read as
    // "Reconnecting…" — falling through to "Ringing.." mid-call was misleading.
    const reconnecting =
        connectionState === ConnectionState.Reconnecting ||
        connectionState === ConnectionState.SignalReconnecting;
    const status =
        reconnecting
            ? 'Reconnecting…'
            : answered
              ? formatDuration(seconds)
              : 'Ringing..';

    return (
        <CallCard
            name={name}
            phone={phone}
            image={image}
            status={status}
            showWaveform={answered}
            popover={pickerOpen ? <DevicePicker room={room} onClose={() => setPickerOpen(false)} /> : null}
            controls={
                <ControlsRow>
                    {/* Device picker stays usable while ringing: the guide wants
                        the right mic / speaker in place before the client picks
                        up, not scrambling for it mid-conversation. */}
                    <PillButton
                        variant="neutral"
                        icon="ellipsis-h"
                        active={pickerOpen}
                        onPress={() => setPickerOpen((o) => !o)}
                    />
                    <PillButton
                        variant={isMicrophoneEnabled ? 'accent' : 'muted'}
                        icon={isMicrophoneEnabled ? 'microphone' : 'microphone-slash'}
                        disabled={!answered}
                        onPress={toggleMute}
                    />
                    <HangupButton onPress={handleHangup} />
                </ControlsRow>
            }
        />
    );
}

// --- Presentational ---------------------------------------------------------

interface CallCardProps {
    name: string;
    /** The number being dialled; hidden when unknown. */
    phone?: string;
    image: string;
    status: string;
    showWaveform?: boolean;
    controls: React.ReactNode;
    popover?: React.ReactNode;
}

function CallCard({ name, phone, image, status, showWaveform, controls, popover }: CallCardProps) {
    return (
        <View style={styles.card}>
            <DragBar />

            <View style={styles.center}>
                <Avatar name={name} image={image} />

                <Text style={styles.name} numberOfLines={1}>
                    {name}
                </Text>
                {phone ? (
                    <Text style={styles.phone} numberOfLines={1} selectable>
                        {phone}
                    </Text>
                ) : null}
                <Text style={styles.status}>{status}</Text>

                {showWaveform ? <Waveform /> : null}
            </View>

            <View style={styles.footer}>
                {popover}
                {controls}
            </View>
        </View>
    );
}

/**
 * Post-call card: compact "Call ended" header over the quality survey. Scrolls
 * because the issue list can outgrow the desktop call window.
 */
function FeedbackCard({
    name,
    image,
    onSubmit,
    onSkip,
}: {
    name: string;
    image: string;
    onSubmit: (feedback: CallFeedback) => Promise<void>;
    onSkip: () => void;
}) {
    return (
        <View style={styles.card}>
            <DragBar />
            <ScrollView style={styles.feedbackScroll} contentContainerStyle={styles.feedbackContent} showsVerticalScrollIndicator={false}>
                <View style={styles.feedbackHeader}>
                    <Avatar name={name} image={image} small />
                    <Text style={styles.name} numberOfLines={1}>
                        {name}
                    </Text>
                    <Text style={styles.status}>Call ended</Text>
                </View>
                <CallFeedbackForm onSubmit={onSubmit} onSkip={onSkip} />
            </ScrollView>
        </View>
    );
}

/** Top strip that lets the frameless desktop window be dragged around. */
function DragBar() {
    const dragRef = useRef<View>(null);

    useEffect(() => {
        const node = dragRef.current as unknown as HTMLElement | null;
        if (node) (node.style as any).webkitAppRegion = 'drag';
    }, []);

    return <View ref={dragRef} style={styles.dragBar} />;
}

function Avatar({ name, image, small }: { name: string; image: string; small?: boolean }) {
    const initial = name.trim().charAt(0).toUpperCase() || '?';
    return image ? (
        <Image source={{ uri: image }} style={[styles.avatar, small && styles.avatarSmall]} />
    ) : (
        <View style={[styles.avatar, styles.avatarFallback, small && styles.avatarSmall]}>
            <Text style={[styles.avatarInitial, small && styles.avatarInitialSmall]}>{initial}</Text>
        </View>
    );
}

const WAVEFORM_BANDS = 28;

/**
 * Live waveform driven by the remote party's audio (the voice you hear), via
 * Web Audio frequency analysis. Bars sit at a small resting height in silence
 * and rise with speech. Must render inside <LiveKitRoom>.
 */
function Waveform() {
    // The bridged WhatsApp/PSTN party's audio track. SIP participants may
    // publish with an unknown source, so match both.
    const trackRefs = useTracks([Track.Source.Microphone, Track.Source.Unknown], {
        onlySubscribed: true,
    });
    const remoteAudio = trackRefs.find(
        (t) => !t.participant.isLocal && t.publication?.kind === Track.Kind.Audio,
    );
    // loPass/hiPass are FFT bin indexes: with the default 2048-point FFT at
    // 48 kHz each bin is ~23 Hz, so 3–170 ≈ 80 Hz – 4 kHz — the voice band.
    // The hook's defaults (100–600 ≈ 2.3–14 kHz) sit above telephone audio,
    // which left the bars nearly frozen.
    const volumes = useMultibandTrackVolume(remoteAudio, {
        bands: WAVEFORM_BANDS,
        loPass: 3,
        hiPass: 170,
    });

    return (
        <View style={styles.waveform}>
            {volumes.map((volume, i) => (
                <WaveBar key={i} volume={volume} />
            ))}
        </View>
    );
}

function WaveBar({ volume }: { volume: number }) {
    const ref = useRef<View>(null);
    useEffect(() => {
        const node = ref.current as unknown as HTMLElement | null;
        if (node) {
            node.style.transition = 'transform 120ms ease-out';
            node.style.transformOrigin = 'center';
        }
    }, []);
    // The hook normalizes on a sqrt-of-dB curve, so real signal crowds into
    // ~0.4 (line/comfort noise) to ~0.85 (speech). Re-expand that slice to the
    // full bar height so the voice visibly drives the bars.
    const level = Math.min(1, Math.max(0, (volume - 0.4) / 0.45));
    const scale = 0.15 + 0.85 * level;
    return <View ref={ref} style={[styles.waveBar, { transform: [{ scaleY: scale }] }]} />;
}

function ControlsRow({ children }: { children: React.ReactNode }) {
    return <View style={styles.controlsRow}>{children}</View>;
}

type PillVariant = 'neutral' | 'accent' | 'muted' | 'danger';

function PillButton({
    variant,
    icon,
    onPress,
    disabled,
    active,
}: {
    variant: PillVariant;
    icon: React.ComponentProps<typeof FontAwesome>['name'];
    onPress?: () => void;
    disabled?: boolean;
    active?: boolean;
}) {
    const palette = PILL_PALETTE[variant];
    return (
        <TouchableOpacity
            style={[styles.pill, { backgroundColor: palette.bg }, active && styles.pillActive, disabled && styles.pillDisabled]}
            activeOpacity={0.8}
            onPress={onPress}
            disabled={disabled || !onPress}
        >
            <FontAwesome name={icon} size={20} color={palette.fg} />
        </TouchableOpacity>
    );
}

function HangupButton({ onPress }: { onPress: () => void }) {
    return (
        <TouchableOpacity
            style={[styles.pill, styles.hangup]}
            activeOpacity={0.85}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel="Hang up"
        >
            <FontAwesome name="phone" size={22} color="#FFF" style={styles.hangupIcon} />
        </TouchableOpacity>
    );
}

// --- Device picker ----------------------------------------------------------

function DevicePicker({ room, onClose }: { room: ReturnType<typeof useRoomContext>; onClose: () => void }) {
    const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
    const [speakers, setSpeakers] = useState<MediaDeviceInfo[]>([]);
    const [activeMic, setActiveMic] = useState<string | undefined>();
    const [activeSpeaker, setActiveSpeaker] = useState<string | undefined>();

    const refresh = useCallback(async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            setMics(devices.filter((d) => d.kind === 'audioinput'));
            setSpeakers(devices.filter((d) => d.kind === 'audiooutput'));
        } catch (e) {
            console.log('enumerateDevices error:', e);
        }
    }, []);

    useEffect(() => {
        refresh();
        const md = navigator.mediaDevices;
        md?.addEventListener?.('devicechange', refresh);
        return () => md?.removeEventListener?.('devicechange', refresh);
    }, [refresh]);

    const select = async (kind: MediaDeviceKind, deviceId: string) => {
        try {
            await room.switchActiveDevice(kind, deviceId);
            if (kind === 'audioinput') setActiveMic(deviceId);
            else setActiveSpeaker(deviceId);
        } catch (e) {
            console.log('switchActiveDevice error:', e);
        }
    };

    const isSelected = (active: string | undefined, deviceId: string) =>
        active ? active === deviceId : deviceId === 'default';

    return (
        <>
            <Pressable style={styles.pickerBackdrop} onPress={onClose} />
            <View style={styles.picker}>
                <Text style={styles.pickerSection}>Microphone</Text>
                {mics.map((d) => (
                    <DeviceRow
                        key={d.deviceId}
                        label={d.label || 'Microphone'}
                        selected={isSelected(activeMic, d.deviceId)}
                        onPress={() => select('audioinput', d.deviceId)}
                    />
                ))}
                <Text style={[styles.pickerSection, styles.pickerSectionSpaced]}>Speakers</Text>
                {speakers.map((d) => (
                    <DeviceRow
                        key={d.deviceId}
                        label={d.label || 'Speaker'}
                        selected={isSelected(activeSpeaker, d.deviceId)}
                        onPress={() => select('audiooutput', d.deviceId)}
                    />
                ))}
            </View>
        </>
    );
}

function DeviceRow({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) {
    return (
        <TouchableOpacity style={styles.deviceRow} activeOpacity={0.7} onPress={onPress}>
            <View style={styles.deviceCheck}>
                {selected ? <FontAwesome name="check" size={13} color={Colors.brand} /> : null}
            </View>
            <Text style={styles.deviceLabel} numberOfLines={2}>
                {label}
            </Text>
        </TouchableOpacity>
    );
}

function formatDuration(totalSeconds: number): string {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

const PILL_PALETTE: Record<PillVariant, { bg: string; fg: string }> = {
    neutral: { bg: '#F1F3F5', fg: Colors.brand },
    accent: { bg: 'rgba(20, 165, 198, 0.14)', fg: Colors.brand },
    muted: { bg: '#FCE9E7', fg: '#E53935' },
    danger: { bg: '#F0564F', fg: '#FFF' },
};

const styles = StyleSheet.create({
    window: {
        flex: 1,
        backgroundColor: '#FFF',
    },
    card: {
        flex: 1,
        backgroundColor: '#FFF',
        alignItems: 'center',
    },
    dragBar: {
        height: 44,
        alignSelf: 'stretch',
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 24,
        gap: 6,
    },
    avatar: {
        width: 168,
        height: 168,
        borderRadius: 84,
        backgroundColor: '#E9ECEF',
        marginBottom: 22,
    },
    avatarFallback: {
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarInitial: {
        fontSize: 64,
        fontWeight: '600',
        color: '#ADB5BD',
    },
    avatarSmall: {
        width: 72,
        height: 72,
        borderRadius: 36,
        marginBottom: 10,
    },
    avatarInitialSmall: {
        fontSize: 28,
    },
    feedbackScroll: {
        alignSelf: 'stretch',
        flex: 1,
    },
    feedbackContent: {
        flexGrow: 1,
        justifyContent: 'center',
        paddingHorizontal: 24,
        paddingBottom: 24,
    },
    feedbackHeader: {
        alignItems: 'center',
        gap: 4,
        marginBottom: 20,
    },
    name: {
        fontSize: 24,
        fontWeight: '700',
        color: '#1A1A1A',
        textAlign: 'center',
    },
    phone: {
        fontSize: 15,
        color: '#5C636B',
        marginTop: -2,
    },
    status: {
        fontSize: 17,
        color: '#8A9099',
    },
    waveform: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 36,
        gap: 3,
        marginTop: 14,
    },
    waveBar: {
        width: 3,
        height: 28,
        borderRadius: 2,
        backgroundColor: Colors.brand,
    },
    footer: {
        alignSelf: 'stretch',
        paddingBottom: 40,
        paddingTop: 8,
    },
    controlsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
    },
    pill: {
        minWidth: 96,
        height: 58,
        borderRadius: 29,
        alignItems: 'center',
        justifyContent: 'center',
    },
    pillActive: {
        borderWidth: 2,
        borderColor: Colors.brand,
    },
    pillDisabled: {
        opacity: 0.45,
    },
    hangup: {
        backgroundColor: '#F0564F',
    },
    hangupIcon: {
        transform: [{ rotate: '135deg' }],
    },
    // Device picker
    pickerBackdrop: {
        position: 'absolute',
        top: -2000,
        left: -2000,
        right: -2000,
        bottom: -2000,
    },
    picker: {
        position: 'absolute',
        bottom: 76,
        left: 24,
        width: 280,
        backgroundColor: '#FFF',
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.18,
        shadowRadius: 16,
        elevation: 10,
    },
    pickerSection: {
        fontSize: 15,
        fontWeight: '700',
        color: '#1A1A1A',
        marginBottom: 4,
    },
    pickerSectionSpaced: {
        marginTop: 10,
    },
    deviceRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
    },
    deviceCheck: {
        width: 16,
        alignItems: 'center',
    },
    deviceLabel: {
        flex: 1,
        fontSize: 15,
        color: '#343A40',
    },
});
